/**
 * Logic Evaluation Service
 * Handles client-side evaluation of survey logic rules
 */

class LogicEvaluationService {
  constructor() {
    this.debugMode = process.env.NODE_ENV === 'development';
  }

  /**
   * Evaluate all logic rules for a survey based on current responses
   * @param {Array} rules - Array of logic rules
   * @param {Object} responses - Current survey responses
   * @param {Array} questions - Array of all survey questions
   * @returns {Object} Evaluation result with actions to execute
   */
  evaluateRules(rules, responses, questions) {
    try {
      const result = {
        questionsToShow: new Set(),
        questionsToHide: new Set(),
        controlledQuestions: new Set(),
        questionsVisibleByDefault: new Set(),
        questionsToJumpTo: null,
        endSurvey: false,
        messagesToShow: [],
        executedRules: [],
        errors: []
      };

      // Create question map for quick lookup
      const questionMap = {};
      (Array.isArray(questions) ? questions : []).forEach(q => {
        questionMap[String(q.id)] = q;
      });

      // Lower priority numbers win; array order breaks ties consistently.
      const sortedRules = (Array.isArray(rules) ? rules : [])
        .map((rule, index) => ({ rule, index }))
        .filter(({ rule }) => rule.enabled !== false && rule.enabled !== 0 && rule.enabled !== '0' && rule.enabled !== 'false')
        .sort((a, b) => (Number(a.rule.priority) || 0) - (Number(b.rule.priority) || 0) || a.index - b.index);
      const visibilityDecisions = new Map();
      let navigationActionChosen = false;
      const questionsWithShowRules = new Set();
      const questionsWithHideRules = new Set();
      sortedRules.forEach(({ rule }) => {
        (Array.isArray(rule.actions) ? rule.actions : []).forEach(action => {
          if (['show_question', 'hide_question'].includes(action?.type) && action.targetId) {
            const targetId = String(action.targetId);
            result.controlledQuestions.add(targetId);
            if (action.type === 'show_question') questionsWithShowRules.add(targetId);
            else questionsWithHideRules.add(targetId);
          }
        });
      });
      questionsWithHideRules.forEach(questionId => {
        if (!questionsWithShowRules.has(questionId)) {
          result.questionsVisibleByDefault.add(questionId);
        }
      });

      // Evaluate each rule
      for (const { rule } of sortedRules) {
        try {
          if (!questionMap[String(rule.source_question_id || rule.sourceQuestionId)]) {
            result.errors.push({ ruleId: rule.id, error: 'Rule source question does not exist' });
            continue;
          }
          const conditionsMet = this.evaluateConditions(rule.conditions, responses, questionMap);
          
          if (conditionsMet) {
            this.executeActions(rule.actions, result, questionMap, visibilityDecisions, Number(rule.priority) || 0, () => {
              if (navigationActionChosen) return false;
              navigationActionChosen = true;
              return true;
            });
            result.executedRules.push({
              id: rule.id,
              name: rule.name,
              conditions: rule.conditions,
              actions: rule.actions
            });

            this.log(`Rule executed: ${rule.name || rule.id}`, rule);
          }
        } catch (error) {
          result.errors.push({
            ruleId: rule.id,
            ruleName: rule.name,
            error: error.message
          });
          this.logError(`Error evaluating rule ${rule.id}:`, error);
        }
      }

      visibilityDecisions.forEach((decision, questionId) => {
        if (decision.type === 'show_question') result.questionsToShow.add(questionId);
        else result.questionsToHide.add(questionId);
      });

      return result;
    } catch (error) {
      this.logError('Error in evaluateRules:', error);
      return {
        questionsToShow: new Set(),
        questionsToHide: new Set(),
        controlledQuestions: new Set(),
        questionsVisibleByDefault: new Set(),
        questionsToJumpTo: null,
        endSurvey: false,
        messagesToShow: [],
        executedRules: [],
        errors: [{ error: error.message }]
      };
    }
  }

  /**
   * Evaluate conditions for a rule
   * @param {Array} conditions - Array of conditions
   * @param {Object} responses - Current survey responses
   * @param {Object} questionMap - Map of question ID to question object
   * @returns {boolean} True if all conditions are met
   */
  evaluateConditions(conditions, responses, questionMap) {
    if (!conditions || conditions.length === 0) {
      return false;
    }

    let result = null;

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];
      const conditionResult = this.evaluateSingleCondition(condition, responses, questionMap);
      
      if (result === null) {
        result = conditionResult;
      } else {
        const logicOperator = condition.logicOperator || 'and';
        result = logicOperator === 'or' ? result || conditionResult : result && conditionResult;
      }
    }

    return result === true;
  }

  /**
   * Evaluate a single condition
   * @param {Object} condition - Single condition object
   * @param {Object} responses - Current survey responses
   * @param {Object} questionMap - Map of question ID to question object
   * @returns {boolean} True if condition is met
   */
  evaluateSingleCondition(condition, responses, questionMap) {
    const { questionId, operator, value } = condition || {};
    const question = questionMap[String(questionId)];
    const response = responses?.[questionId];

    if (!question) {
      this.logError(`Question not found: ${questionId}`);
      return false;
    }

    // Handle different operators
    switch (operator) {
      case 'answered':
        return this.isAnswered(response);
      
      case 'not_answered':
        return !this.isAnswered(response);
      
      case 'equals':
        return this.evaluateEquals(response, value, question.type);
      
      case 'not_equals':
        return this.isAnswered(response) && !this.evaluateEquals(response, value, question.type);
      
      case 'contains':
        return this.evaluateContains(response, value);
      
      case 'not_contains':
        return this.isAnswered(response) && !this.evaluateContains(response, value);
      
      case 'starts_with':
        return this.evaluateStartsWith(response, value);
      
      case 'ends_with':
        return this.evaluateEndsWith(response, value);
      
      case 'greater_than':
        return this.evaluateGreaterThan(response, value, question.type);
      
      case 'less_than':
        return this.evaluateLessThan(response, value, question.type);
      
      case 'between':
        return this.evaluateBetween(response, value, question.type);
      
      case 'selected':
        return this.evaluateSelected(response, value, question.type);
      
      case 'not_selected':
        return this.isAnswered(response) && !this.evaluateSelected(response, value, question.type);
      
      case 'any_selected':
        return this.evaluateAnySelected(response, value);
      
      case 'all_selected':
        return this.evaluateAllSelected(response, value);
      
      case 'none_selected':
        return this.evaluateNoneSelected(response);
      
      default:
        this.logError(`Unknown operator: ${operator}`);
        return false;
    }
  }

  /**
   * Execute actions based on rule evaluation
   * @param {Array} actions - Array of actions to execute
   * @param {Object} result - Result object to modify
   * @param {Object} questionMap - Map of question ID to question object
   */
  executeActions(actions, result, questionMap, visibilityDecisions, priority, chooseNavigationAction) {
    if (!Array.isArray(actions)) {
      result.errors.push({ error: 'Rule actions must be an array' });
      return;
    }

    for (const action of actions) {
      if (!action || typeof action !== 'object') {
        result.errors.push({ error: 'Rule contains an invalid action' });
        continue;
      }

      switch (action.type) {
        case 'show_question':
        case 'hide_question':
          if (!action.targetId || !questionMap[String(action.targetId)]) {
            result.errors.push({ error: `Invalid question target for ${action.type}`, targetId: action.targetId });
            break;
          }
          const targetId = String(action.targetId);
          const currentDecision = visibilityDecisions.get(targetId);
          if (!currentDecision || priority < currentDecision.priority) {
            visibilityDecisions.set(targetId, { type: action.type, priority });
          } else if (priority === currentDecision.priority && currentDecision.type !== action.type) {
            // Conflicting actions at the same priority fail closed.
            visibilityDecisions.set(targetId, { type: 'hide_question', priority });
          }
          break;
        
        case 'jump_to':
          if (!action.targetId || !questionMap[String(action.targetId)]) {
            result.errors.push({ error: 'Invalid question target for jump_to', targetId: action.targetId });
          } else if (chooseNavigationAction()) {
            result.questionsToJumpTo = String(action.targetId);
          }
          break;
        
        case 'end_survey':
          if (chooseNavigationAction()) result.endSurvey = true;
          break;
        
        case 'show_message':
          if (action.value) {
            result.messagesToShow.push({
              message: action.value,
              type: action.messageType || 'info'
            });
          }
          break;
        
        default:
          this.logError(`Unknown action type: ${action.type}`);
          result.errors.push({ error: `Unknown action type: ${action.type}` });
      }
    }

  }

  // Condition evaluation helper methods

  isAnswered(response) {
    if (response === null || response === undefined) return false;
    if (typeof response === 'string') return response.trim() !== '';
    if (Array.isArray(response)) return response.length > 0;
    if (typeof response === 'object') return Object.keys(response).length > 0;
    return true;
  }

  evaluateEquals(response, value, questionType) {
    if (!this.isAnswered(response)) return false;
    if (Array.isArray(response)) {
      return response.some(item => this.evaluateEquals(item, value, questionType));
    }
    
    // Handle different question types
    switch (questionType) {
      case 'number':
        return parseFloat(response) === parseFloat(value);
      case 'date':
      case 'datetime':
        return new Date(response).getTime() === new Date(value).getTime();
      default:
        return String(response).toLowerCase() === String(value).toLowerCase();
    }
  }

  evaluateContains(response, value) {
    if (!this.isAnswered(response)) return false;
    if (Array.isArray(response)) {
      return response.some(item => String(item).toLowerCase().includes(String(value).toLowerCase()));
    }
    return String(response).toLowerCase().includes(String(value).toLowerCase());
  }

  evaluateStartsWith(response, value) {
    if (!this.isAnswered(response)) return false;
    return String(response).toLowerCase().startsWith(String(value).toLowerCase());
  }

  evaluateEndsWith(response, value) {
    if (!this.isAnswered(response)) return false;
    return String(response).toLowerCase().endsWith(String(value).toLowerCase());
  }

  evaluateGreaterThan(response, value, questionType) {
    if (!this.isAnswered(response)) return false;
    const [numResponse, numValue] = this.comparableValues(response, value, questionType);
    return !isNaN(numResponse) && !isNaN(numValue) && numResponse > numValue;
  }

  evaluateLessThan(response, value, questionType) {
    if (!this.isAnswered(response)) return false;
    const [numResponse, numValue] = this.comparableValues(response, value, questionType);
    return !isNaN(numResponse) && !isNaN(numValue) && numResponse < numValue;
  }

  evaluateBetween(response, value, questionType) {
    if (!this.isAnswered(response)) return false;
    
    if (Array.isArray(value) && value.length === 2) {
      const [numResponse, min] = this.comparableValues(response, value[0], questionType);
      const [, max] = this.comparableValues(response, value[1], questionType);
      return !isNaN(numResponse) && !isNaN(min) && !isNaN(max) && 
             numResponse >= min && numResponse <= max;
    }
    
    return false;
  }

  evaluateSelected(response, value, questionType) {
    if (!this.isAnswered(response)) return false;
    if (typeof response === 'string' && response.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(response);
        if (Array.isArray(parsed)) response = parsed;
      } catch (error) {
        this.logError('Unable to parse selected answer JSON:', error);
      }
    }
    
    switch (questionType) {
      case 'multiple-choice':
        return String(response) === String(value);
      
      case 'checkboxes':
        if (Array.isArray(response)) {
          return response.includes(String(value));
        }
        return String(response) === String(value);
      
      case 'dropdown':
        return String(response) === String(value);
      
      default:
        return String(response) === String(value);
    }
  }

  evaluateAnySelected(response, values) {
    if (!this.isAnswered(response) || !Array.isArray(values)) return false;
    response = this.normalizeSelection(response);
    
    if (Array.isArray(response)) {
      return values.some(value => response.includes(String(value)));
    }
    
    return values.includes(String(response));
  }

  evaluateAllSelected(response, values) {
    if (!this.isAnswered(response) || !Array.isArray(values)) return false;
    response = this.normalizeSelection(response);
    
    if (Array.isArray(response)) {
      return values.every(value => response.includes(String(value)));
    }
    
    return values.length === 1 && values[0] === String(response);
  }

  evaluateNoneSelected(response) {
    return !this.isAnswered(response) || this.normalizeSelection(response).length === 0;
  }

  comparableValues(response, value, questionType) {
    if (questionType === 'date' || questionType === 'datetime') {
      return [new Date(response).getTime(), new Date(value).getTime()];
    }
    return [Number(response), Number(value)];
  }

  normalizeSelection(response) {
    if (Array.isArray(response)) return response.map(String);
    if (typeof response === 'string' && response.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(response);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch (error) {
        this.logError('Unable to parse selected answer JSON:', error);
      }
    }
    return [String(response)];
  }

  // Utility methods

  log(message, data = null) {
    if (this.debugMode) {
      console.log(`[LogicEvaluationService] ${message}`, data);
    }
  }

  logError(message, error = null) {
    console.error(`[LogicEvaluationService] ${message}`, error);
  }

  /**
   * Get question visibility based on logic evaluation
   * @param {Array} questions - All survey questions
   * @param {Object} evaluationResult - Result from evaluateRules
   * @returns {Object} Map of question ID to visibility status
   */
  getQuestionVisibility(questions, evaluationResult) {
    const visibility = {};
    const controlledQuestions = evaluationResult.controlledQuestions || new Set();
    const visibleByDefault = evaluationResult.questionsVisibleByDefault || new Set();
    
    // Start with all questions visible by default
    questions.forEach(question => {
      visibility[question.id] = !controlledQuestions.has(String(question.id)) ||
        visibleByDefault.has(String(question.id));
    });
    
    // Apply hide actions
    evaluationResult.questionsToHide.forEach(questionId => {
      visibility[questionId] = false;
    });
    
    // Apply show actions (override hide if both are present)
    evaluationResult.questionsToShow.forEach(questionId => {
      visibility[questionId] = true;
    });
    
    return visibility;
  }

  /**
   * Check if survey should end based on logic evaluation
   * @param {Object} evaluationResult - Result from evaluateRules
   * @returns {boolean} True if survey should end
   */
  shouldEndSurvey(evaluationResult) {
    return evaluationResult.endSurvey;
  }

  /**
   * Get next question to jump to
   * @param {Object} evaluationResult - Result from evaluateRules
   * @returns {string|null} Question ID to jump to, or null
   */
  getJumpTarget(evaluationResult) {
    return evaluationResult.questionsToJumpTo;
  }

  /**
   * Get messages to display
   * @param {Object} evaluationResult - Result from evaluateRules
   * @returns {Array} Array of messages to display
   */
  getMessages(evaluationResult) {
    return evaluationResult.messagesToShow || [];
  }
}

export default LogicEvaluationService;